<template>    
  <div>
    <v-main>
      <v-container>
        <v-row>
          <v-col 
            cols="12"
            xs="12"
            md="6"
          >
            <div class="text-h4">
              {{ $t("cotacoes") }} <small v-show="!listLoading">({{ listCount }})</small>
            </div>
          </v-col>
          <v-col
            cols="12"
            xs="12"
            md="6"
            class="align-right"
          >
            <router-link 
              :to="`/${route}/new`" 
              tag="button"
            >
              <v-btn
                color="primary"
                small
              >
                <v-icon left>
                  mdi-plus-circle
                </v-icon>
                {{ $t('novaCotacao') }}
              </v-btn>
            </router-link>
          </v-col>
        </v-row>
        <v-skeleton-loader
          v-if="listLoading"
          :loading="listLoading"
          transition="fade-transition"
          type="table"
        />
        <v-card v-show="!listLoading">
          <v-container>
            <v-data-table
              :headers="headers"
              :items="list"
              :items-per-page="itemsPerPage"
              :server-items-length="listCount"
              :page.sync="page"
              :options.sync="options"
              hide-default-footer
            >
              <template v-slot:no-data>
                {{ $t("naoHaDadosDisponiveis") }}
              </template>
              <template v-slot:top>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="$t('filtro')"
                  :placeholder="$t('filtreQualquerCampoPressioneEnter')"
                  class="mx-4"
                  @keyup="onEnterFilter"
                />
              </template>
              <template v-slot:item.SALES_ORDER_ID="{ item }">
                <div class="min-width-70">
                  <router-link :to="`/${route}/view/${item.SALES_ORDER_ID}`">
                    <span v-if="item.ID_IMPORTACAO === 'R'">{{ $t('cotacao') }}</span>
                    <span v-else>{{ $t('pedido') }}</span>
                    {{ item.SALES_ORDER_ID }}
                  </router-link>
                </div>
              </template>
              <template v-slot:item.CREATION_DATE="{ item }">
                <div>
                  {{ item.CREATION_DATE | dateToUSNoHour }}
                </div>
              </template>
              <template v-slot:item.ID_IMPORTACAO="{ item }">
                <div v-if="item.STATUS === 'A'">
                  <v-chip
                    v-for="status in statusQuote"
                    v-show="status.id === item.ID_IMPORTACAO"
                    :key="status.id"
                    :color="status.class"
                    class="margin-1"
                    text-color="white"
                    small
                  >
                    <span>{{ status.name }}</span>
                  </v-chip>
                </div>
                <div v-else-if="item.STATUS === 'R'">
                  <v-chip
                    color="error"
                    class="margin-1"
                    text-color="white"
                    small
                  >
                    <span>{{ $t('reprovado') }}</span>
                  </v-chip>
                </div>
                <div v-else>
                  <v-chip
                    color="grey"
                    class="margin-1"
                    text-color="white"
                    small
                  >
                    <span>{{ $t('pendente') }}</span>
                  </v-chip>
                </div>
              </template>
              <template v-slot:item.TOTAL="{ item }">
                <div>
                  {{ item.TOTAL | currencyUSA(3) }}
                </div>
              </template>
              <template v-slot:item.ACOES="{ item }">
                <div v-if="(item.ID_IMPORTACAO === 'R' && item.STATUS === 'A') || item.STATUS === 'R'">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    :to="`/${route}/edit/${item.SALES_ORDER_ID}`"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:footer>
                <v-pagination
                  v-if="listCount"
                  v-model="page"
                  :length="pageCount(listCount)"
                  :total-visible="9"
                  :disabled="loading"
                  @input="onPageChange"
                />
              </template>
            </v-data-table>
          </v-container>
        </v-card>
        <br>
      </v-container>
    </v-main>
  </div>
</template>
<script>
import { mapState, mapActions } from "vuex";
import { currencyUSA, dateToUSNoHour } from "../../helpers/filter";
import EventBus from "../../plugins/event-bus";

const pk = "ID";
const route = "quotes";
const appStore = "AppQuotesStore";
const defaultSort = "CREATION_DATE";
const defaultSortDesc = true;

export default {
	data () {
    return {
      loading: true,
      page: parseInt(localStorage.getItem('pageQuotes')) || 1,
      itemsPerPage: 10,
      options: {},
      search: "",
      route: route,
      rowsPerPageItems: [10, 25, 50],
      statusQuote: [
        { id: 'N', name: this.$i18n.t("naoImportado"), class: "warning" },
        { id: 'Y', name: this.$i18n.t("importado"), class: "primary" },
        { id: 'E', name: this.$i18n.t("erroImportacao"), class: "error" },
        { id: 'R', name: this.$i18n.t("cotacao"), class: "purple" },
        { id: 'L', name: this.$i18n.t("importando"), class: "grey" }
      ],
      headers: [
        { text: this.$i18n.t("numPedido"), value: "SALES_ORDER_ID" },
        { text: this.$i18n.t("data"), value: "CREATION_DATE" },
        { text: this.$i18n.t("cliente"), value: "CUSTOMER_NAME" },
        { text: this.$i18n.t("ordemCompra"), value: "CUST_PO_NUMBER" },
        { text: this.$i18n.t("repVendas"), value: "SALES_REP_NAME" },
        { text: this.$i18n.t("status"), value: "ID_IMPORTACAO" },
        { text: this.$i18n.t("total"), value: "TOTAL", sortable: false },
        { text: this.$i18n.t("acoes"), value: "ACOES", sortable: false }
      ]
    }
  },
  computed: {
    ...mapState(`${appStore}`, {
      list: state => state[route],
      listCount: state => state[`${route}Qtd`],
      listLoading: (state) => state[`${route}Loading`]
    })
  },
  watch: {
    options: {
      handler () {
        if (this.options.sortBy.length == 0){
          this.options.sortBy = [defaultSort];
          this.options.sortDesc = [defaultSortDesc];
        } else {
          this.$store.dispatch(`${appStore}/getAll`, this.options);
        }
      },
      deep: true
    }
  },
	created() {
    EventBus.$on("change::isLoading", (loading) => {
      this.loading = loading;
    });
  },
  mounted() {
    this.$store.commit(`${appStore}/cleanState`);
  },
  methods: {
    currencyUSA,
    dateToUSNoHour,
    ...mapActions([`${appStore}/getAll`]),
    open(item){
      this.$router.push({
        path: `/${route}/${item[pk]}`
      });
    },
    pageCount(totalItems) {
      return Math.ceil(totalItems / this.itemsPerPage);
    },
    onPageChange(page) {
      localStorage.setItem('pageQuotes', page);
    },
    onEnterFilter(event) {
      if (event.keyCode === 13) {
        this.options.search = this.search;
        this.$store.dispatch(`${appStore}/getAll`, this.options);
      }
    }
  }
}
</script>